import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import { SR_API_ACTION_UTILITY_PARTNER_PROVIDER_NOTES_CHECK_ADDRESS_NOTES } from 'api/utilityActionTypes';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';

import requestUtilityApi from 'client/actions/requestUtilityApi';
import useDispatch from 'client/hooks/useDispatch';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_RED,
  SR_BUTTON_THEME_ADOBE_YELLOW,
  SR_BUTTON_HEIGHT_MEDIUM,
} from 'client/components/SrButton/SrButton';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import { isHomebuyerPartner } from 'common/utility/helpers';

const getFormFields = ({ provider, utilityPartner }) =>
  [
    provider && {
      id: 'providerTitle',
      title: 'Search utility company',
      type: 'short_text',
      properties: {
        disabled: true,
      },
    },
    {
      id: 'note',
      title: isHomebuyerPartner(utilityPartner)
        ? 'Default utility note for clients'
        : 'Default utility note for tenants',
      subtitle: (
        <>
          <SrText font="greycliff" weight="500" lineHeight="25px" fontSize="16px">
            {isHomebuyerPartner(utilityPartner)
              ? 'Write default note for clients'
              : 'Write default note for tenants'}
          </SrText>
          <SrBox paddingTop="10px">
            <SrText
              font="greycliff"
              weight="500"
              lineHeight="25px"
              fontSize="16px"
              color="orange300"
            >
              Any notes you have already written for this provider on the individual address level
              will be overwritten by the default note below.
            </SrText>
          </SrBox>
        </>
      ),
      type: 'long_text',
      properties: {
        placeholder: isHomebuyerPartner(utilityPartner)
          ? 'Enter note for client here...'
          : 'Enter note for tenant here...',
        style: {
          height: '140px',
        },
      },
      validations: ['required'],
    },
  ].filter(Boolean);

const UpEditProviderNoteModal = ({
  partnerId,
  utilityPartner,
  provider,
  defaultNote,
  isNew,
  onSubmit,
  onCancel,
  onDelete,
}) => {
  const [showAddressNotesWarning, setShowAddressNotesWarning] = useState(false);
  const formFields = getFormFields({ provider, utilityPartner });
  const dispatch = useDispatch();

  return (
    <SrDarkModal>
      <SrModalContent width={412} innerStyle={{ padding: 0 }}>
        <SrBox textAlign="center" borderBottom="2px solid #e9ebec" padding="20px 0">
          <SrText
            font="greycliff"
            weight="800"
            lineHeight="25px"
            fontSize="25px"
            display="block"
            paddingBottom="8px"
          >
            {isNew ? 'Add Default Note' : 'Edit Default Note'}
          </SrText>
          <SrBox padding="0 20px">
            <SrText font="greycliff" weight="400" lineHeight="25px" fontSize="16px">
              {`This default note will be displayed to ${
                isHomebuyerPartner(utilityPartner) ? 'clients' : 'tenants'
              }${
                provider?.title ? ' who use the selected utility company below' : ''
              }`}
            </SrText>
          </SrBox>
        </SrBox>

        <SrBox padding="0 40px 40px">
          <Formik
            initialValues={createInitialValues({
              formFields,
              defaultValues: {
                note: defaultNote,
                providerTitle: provider?.title || '',
              },
            })}
            validate={createFormValidationFn(formFields)}
            onSubmit={onSubmit}
          >
            {(formikBag) => {
              const handleSubmit = () => {
                dispatch(requestUtilityApi, {
                  action: SR_API_ACTION_UTILITY_PARTNER_PROVIDER_NOTES_CHECK_ADDRESS_NOTES,
                  payload: {
                    utilityProviderId: provider.id,
                    utilityPartnerId: partnerId,
                  },
                }).then(({ hasAddressNotes }) => {
                  if (hasAddressNotes) {
                    setShowAddressNotesWarning(true);
                  } else {
                    formikBag.handleSubmit();
                  }
                });
              };
              return (
                <Form>
                  {showAddressNotesWarning && (
                    <SrSimpleMessageModal
                      theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
                      headline="Overwrite old notes?"
                      maxWidth={440}
                      message={
                        'You have already written notes for this provider on specific addresses in ' +
                        'the past. Saving this default note will overwrite those default notes.'
                      }
                      srMultiButtons={[
                        {
                          buttonText: 'Confirm',
                          onButtonClick: () => {
                            setShowAddressNotesWarning(false);
                            formikBag.handleSubmit();
                          },
                          buttonTheme: SR_BUTTON_THEME_ADOBE_YELLOW,
                        },
                        {
                          buttonText: 'Cancel',
                          onButtonClick: () => {
                            setShowAddressNotesWarning(false);
                          },
                          buttonTheme: SR_BUTTON_THEME_ADOBE_GREY,
                        },
                      ]}
                    />
                  )}
                  {formFields.map((formField) => (
                    <SrFormikField
                      key={formField.id}
                      formField={formField}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                    />
                  ))}

                  <SrBox paddingTop="28px">
                    <SrButton
                      theme={SR_BUTTON_THEME_ADOBE_GREEN}
                      disabled={!formikBag.isValid || !formikBag.dirty}
                      heightType={SR_BUTTON_HEIGHT_MEDIUM}
                      hasBoxShadow
                      type="button"
                      onClick={handleSubmit}
                    >
                      Save
                    </SrButton>
                  </SrBox>
                  {onDelete && !isNew && (
                    <SrBox paddingTop="20px">
                      <SrButton
                        theme={SR_BUTTON_THEME_ADOBE_RED}
                        heightType={SR_BUTTON_HEIGHT_MEDIUM}
                        hasBoxShadow
                        onClick={onDelete}
                        type="button"
                      >
                        Delete
                      </SrButton>
                    </SrBox>
                  )}
                  <SrBox paddingTop="20px">
                    <SrButton
                      theme={SR_BUTTON_THEME_ADOBE_GREY}
                      heightType={SR_BUTTON_HEIGHT_MEDIUM}
                      hasBoxShadow
                      onClick={onCancel}
                      type="button"
                    >
                      Cancel
                    </SrButton>
                  </SrBox>
                </Form>
              );
            }}
          </Formik>
        </SrBox>
      </SrModalContent>
    </SrDarkModal>
  );
};

UpEditProviderNoteModal.propTypes = {
  partnerId: PropTypes.number.isRequired,
  utilityPartner: PropTypes.object.isRequired,
  provider: PropTypes.object,
  defaultNote: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
};

export default UpEditProviderNoteModal;
